import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Tag } from 'lucide-react';

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form types
  const [formTypes, setFormTypes] = useState([]);
  const [showAddType, setShowAddType] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchFormTypes = useCallback(() => {
    api.get('/form-types').then(r => setFormTypes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/settings').then(r => setSettings(r.data)).catch(() => {}).finally(() => setLoading(false));
    fetchFormTypes();
  }, [fetchFormTypes]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/settings', {
        company_name: settings.company_name,
        currency: settings.currency,
        approval_threshold: settings.approval_threshold,
        require_l2_above_threshold: settings.require_l2_above_threshold,
      });
      setSettings(res.data);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) { toast.error('Type name is required'); return; }
    setAddingType(true);
    try {
      await api.post('/form-types', { name: newTypeName.trim() });
      toast.success(`Form type "${newTypeName.trim()}" added`);
      setNewTypeName('');
      setShowAddType(false);
      fetchFormTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    } finally {
      setAddingType(false);
    }
  };

  const handleDeleteType = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/form-types/${deleteTarget.id}`);
      toast.success(`Form type "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      fetchFormTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="font-data text-sm text-muted-foreground">Loading...</p></div>;
  if (!settings) return null;

  return (
    <div data-testid="settings-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">System configuration</p>
        </div>
        <Button data-testid="save-settings-btn" className="rounded-sm" onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      <div className="space-y-6 max-w-2xl">
        {/* General */}
        <Card className="border border-border rounded-sm shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base font-semibold">General</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Company Name</Label>
              <Input data-testid="company-name-input" value={settings.company_name || ''} onChange={e => setSettings({ ...settings, company_name: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Currency</Label>
              <Input data-testid="currency-input" value={settings.currency || 'INR'} className="rounded-sm" disabled />
            </div>
          </CardContent>
        </Card>

        {/* Form Types Master List */}
        <Card className="border border-border rounded-sm shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-base font-semibold">Form Types</CardTitle>
              <Button variant="outline" size="sm" className="rounded-sm text-xs" onClick={() => setShowAddType(true)} data-testid="add-form-type-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Type
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-4">Manage the master list of form types available when registering webhook forms</p>
            {formTypes.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No form types configured</p>
            ) : (
              <div className="space-y-2">
                {formTypes.map(ft => (
                  <div key={ft.id} data-testid={`form-type-row-${ft.id}`} className="flex items-center justify-between bg-slate-50 border border-border rounded-sm px-4 py-3 hover:bg-slate-100 transition-colors duration-150">
                    <div className="flex items-center gap-3">
                      <Tag className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm font-medium">{ft.name}</span>
                      <Badge variant="outline" className="text-[10px] rounded-sm font-data">{ft.slug}</Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 rounded-sm text-muted-foreground hover:text-red-600 hover:bg-red-50"
                      onClick={() => setDeleteTarget(ft)}
                      data-testid={`delete-type-${ft.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Approval Workflow */}
        <Card className="border border-border rounded-sm shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base font-semibold">Approval Workflow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">L2 Approval Threshold (INR)</Label>
              <Input
                data-testid="threshold-input"
                type="number"
                value={settings.approval_threshold || 50000}
                onChange={e => setSettings({ ...settings, approval_threshold: Number(e.target.value) })}
                className="rounded-sm font-data"
              />
              <p className="text-xs text-muted-foreground">Submissions with amount above this threshold require admin (L2) approval after manager review</p>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Require L2 Approval</p>
                <p className="text-xs text-muted-foreground">Enable multi-level approval for high-value submissions</p>
              </div>
              <Switch
                data-testid="l2-approval-switch"
                checked={settings.require_l2_above_threshold ?? true}
                onCheckedChange={v => setSettings({ ...settings, require_l2_above_threshold: v })}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Form Type Dialog */}
      <Dialog open={showAddType} onOpenChange={setShowAddType}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Form Type</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Add a new type that will be available when registering webhook forms
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Type Name</Label>
              <Input
                data-testid="new-type-name-input"
                value={newTypeName}
                onChange={e => setNewTypeName(e.target.value)}
                placeholder="e.g. Quarterly Review"
                className="rounded-sm"
                onKeyDown={e => e.key === 'Enter' && handleAddType()}
              />
              {newTypeName.trim() && (
                <p className="text-xs text-muted-foreground">Slug: <span className="font-data">{newTypeName.trim().toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '_')}</span></p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setShowAddType(false); setNewTypeName(''); }}>Cancel</Button>
            <Button data-testid="confirm-add-type-btn" className="rounded-sm" onClick={handleAddType} disabled={addingType || !newTypeName.trim()}>
              {addingType ? 'Adding...' : 'Add Type'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Form Type Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Form Type</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>? This type will no longer be available when registering new forms. Existing forms using this type will keep their current type.
              <br /><br />
              <span className="text-red-600 font-medium">Note: This cannot be undone if no forms are using this type. If forms are using it, deletion will be blocked.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-type-btn"
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteType}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Type'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
